import React, { useState } from 'react';
import { useJobs } from '../hooks/useJobs';
import { Button } from '../primitives/Button';
import { JOB_ADVANCEMENTS } from '../../../shared/classes/JobAdvancements';
import type { JobClass } from '../../../shared/CharacterTypes';
import { PLAYER_UI, playerPanel, formatCallingName, getCallingMeta } from '../theme';

function formatStatBonuses(bonuses?: Record<string, number>): string {
  if (!bonuses) return '';
  const labels: Record<string, string> = {
    strength: 'STR',
    agility: 'AGI',
    intelligence: 'INT',
    wisdom: 'WIS',
    defense: 'DEF',
    attack: 'ATK',
    dexterity: 'DEX',
    vitality: 'VIT',
    criticalRate: 'CRIT',
  };
  return Object.entries(bonuses)
    .map(([stat, val]) => {
      const name = labels[stat] ?? stat.toUpperCase();
      const formatted = typeof val === 'number' && val < 1 && val > 0 ? `+${Math.round(val * 100)}%` : `+${val}`;
      return `${formatted} ${name}`;
    })
    .join(' · ');
}

export const JobsContent: React.FC = () => {
  const { currentJob, className, level, paths, advanceJob } = useJobs();
  const [pendingJob, setPendingJob] = useState<JobClass | null>(null);

  const calling = getCallingMeta(className);
  const specialty = currentJob ? JOB_ADVANCEMENTS[currentJob] : undefined;
  const currentReqLevel = specialty?.requiredLevel ?? 1;

  const handleAdvance = (jobId: JobClass) => {
    setPendingJob(jobId);
    advanceJob(jobId);
  };

  return (
    <section style={{ color: PLAYER_UI.text, fontFamily: PLAYER_UI.bodyFont, display: 'grid', gap: 18 }} aria-label="Your calling">
      <header style={{ ...playerPanel(), padding: 22, display: 'flex', gap: 18, alignItems: 'center' }} data-testid="jobs-header">
        <div aria-hidden="true" style={{ width: 64, height: 64, display: 'grid', placeItems: 'center', fontSize: 36, background: PLAYER_UI.well, border: `1px solid ${PLAYER_UI.border}`, borderRadius: 16 }}>
          {calling?.icon ?? '◇'}
        </div>
        <div>
          <span style={{ color: PLAYER_UI.brassBright, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Your calling</span>
          <h2 style={{ margin: '6px 0', fontFamily: PLAYER_UI.displayFont, fontSize: 24 }}>
            {className ? formatCallingName(className) : 'No character selected'}
          </h2>
          <div style={{ color: PLAYER_UI.secondary, fontSize: 13 }}>
            Level {level}{specialty && specialty.requiredLevel > 1 ? ` · ${specialty.name}` : ''}
          </div>
        </div>
      </header>

      {calling && <p style={{ color: PLAYER_UI.secondary, fontSize: 14, lineHeight: 1.7, margin: 0 }}>{calling.description}</p>}

      <div>
        <h3 style={{ fontFamily: PLAYER_UI.displayFont, fontSize: 18, margin: '0 0 8px' }}>Paths ahead</h3>
        <p style={{ color: PLAYER_UI.muted, fontSize: 13, lineHeight: 1.5, margin: 0 }}>
          Advance your calling to unlock powerful class specializations, unique combat abilities, and stat enhancements.
        </p>
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        {paths.map(job => {
          const adv = JOB_ADVANCEMENTS[job.id];
          const isCurrent = currentJob === job.id;
          const isMastered = !isCurrent && currentReqLevel > job.requiredLevel;
          const canAdvance = job.available && !isCurrent && !isMastered;
          const statText = formatStatBonuses(adv?.statBonuses);
          const abilityNames = adv?.abilities?.map(a => a.name).join(', ');

          let badgeText = `Requires Level ${job.requiredLevel}`;
          let badgeColor: string = PLAYER_UI.muted;
          if (isCurrent) {
            badgeText = 'Current Specialization';
            badgeColor = PLAYER_UI.brassBright;
          } else if (isMastered) {
            badgeText = 'Mastered';
            badgeColor = PLAYER_UI.secondary;
          } else if (canAdvance) {
            badgeText = 'Ready to Advance';
            badgeColor = PLAYER_UI.brassBright;
          }

          return (
            <article
              key={job.id}
              style={{
                padding: 18,
                border: `1px solid ${isCurrent || canAdvance ? PLAYER_UI.border : PLAYER_UI.softBorder}`,
                background: isCurrent ? PLAYER_UI.inkRaised : PLAYER_UI.well,
                borderRadius: 12,
              }}
              data-testid={`job-card-${job.id}`}
            >
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, fontSize: 17, color: PLAYER_UI.text }}>{job.name}</h4>
                <span style={{ color: badgeColor, fontSize: 12, fontWeight: canAdvance || isCurrent ? 600 : 400 }}>
                  {badgeText}
                </span>
              </div>

              <p style={{ margin: '10px 0', color: PLAYER_UI.secondary, fontSize: 13, lineHeight: 1.6 }}>
                {job.description}
              </p>

              {(statText || abilityNames) && (
                <div style={{ margin: '8px 0', fontSize: 12, color: PLAYER_UI.brassBright, display: 'grid', gap: 4 }}>
                  {statText && <div>Bonus: {statText}</div>}
                  {abilityNames && <div style={{ color: PLAYER_UI.secondary }}>Abilities: {abilityNames}</div>}
                </div>
              )}

              <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${PLAYER_UI.softBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ fontSize: 12, color: PLAYER_UI.muted }}>
                  {isCurrent
                    ? 'Active'
                    : isMastered
                    ? `Level ${job.requiredLevel} path (Completed)`
                    : job.available
                    ? `Level ${job.requiredLevel} path`
                    : `Requires Level ${job.requiredLevel}`}
                </div>

                {canAdvance && (
                  <Button
                    size="sm"
                    variant="brass"
                    onClick={() => handleAdvance(job.id)}
                    data-testid={`advance-button-${job.id}`}
                  >
                    {pendingJob === job.id ? 'Advancing…' : `Advance to ${job.name}`}
                  </Button>
                )}
                {isCurrent && (
                  <span style={{ fontSize: 12, color: PLAYER_UI.brassBright, fontWeight: 600 }}>
                    Active
                  </span>
                )}
              </div>
            </article>
          );
        })}
        {className && paths.length === 0 && (
          <p style={{ color: PLAYER_UI.muted }}>No further paths are listed for this calling.</p>
        )}
      </div>
    </section>
  );
};
